import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Backdrop,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Box,
  TextField,
  Pagination,
  FormControl,
  TableSortLabel,
  Autocomplete,
} from '@mui/material';
import HttpService from '../../../hooks/Https-services';
import PAGE_ROUTES, {
  API_ROUTES,
  ROLE_MAP_filter,
} from '../../../utils/constant';
import { useBoolean } from '../../../hooks/use-boolean';
import usePopover from '../../../hooks/use-popover';
import { ConfirmDialog } from '../../../components/custom-dialog';
import Iconify from '../../../components/iconify';
import { useRouter } from '../../../hooks/use-router';
import { toast } from 'react-toastify';
import ShiftQuickUpdateForm from '../QuickUpdateDetails';
import { visuallyHidden } from '@mui/utils';
import dayjs from 'dayjs';
interface Game {
  name: string;
  email: any;
  role: any;
  team: string;
  shiftName: any;
  // Add other relevant fields here
}

export default function ShiftTable() {
  const [shifts, setShifts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [openRowId, setOpenRowId] = React.useState('');
  const [shiftToDelete, setShiftToDelete] = React.useState<string | null>(null);
  const [currentShift, setCurrentShift] = React.useState<any | null>(null); // Add state for current user
  const confirm = useBoolean();
  const quickEditDetails = useBoolean();
  const popover = usePopover();
  const router = useRouter();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(10);
  const itemsPerPage = 10;
  const [_searchQuery, _setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState(_searchQuery);
  const [orderBy, setOrderBy] = React.useState<any>(null); // default sort by 'priority'
  const [order, setOrder] = React.useState<any>(null); // default sort order 'asc'
  const [teams, setTeams] = React.useState<any>([]);

  const fetchTeamsList = React.useCallback(async () => {
    try {
      const response = await HttpService.get(API_ROUTES.fatchAllTeam);
      setTeams(response.data.data);
    } catch (error: any) {
      console.error('Error fetching teams list:', error);
    }
  }, []);

  // Call fetchTeamsList only once on component mount
  React.useEffect(() => {
    fetchTeamsList();
  }, [fetchTeamsList]);
  const [selectedRoles, setSelectedRoles] = React.useState<any[]>([]);
  const [selectedRoleKeys, setSelectedRoleKeys] = React.useState<any[]>([]);
  const [selectedTeams, setSelectedTeams] = React.useState<any[]>([]);
  const [selectedTeamsKeys, setSelectedTeamsKeys] = React.useState<any[]>([]);

  const handleChangeTeams = (event: any, newValue: any) => {
    setCurrentPage(1);
    setSelectedTeams(newValue);
    const selectedKeys = newValue.map((option: any) => option.id);
    setSelectedTeamsKeys(selectedKeys);
  };

  const handleChangeRole = (event: any, newValue: any) => {
    setCurrentPage(1);
    setSelectedRoles(newValue);
    const selectedKeys = newValue.map((option: { key: number }) => option.key);
    setSelectedRoleKeys(selectedKeys);
  };

  const handleSearchChange = (e: any) => {
    setDebouncedQuery(e.target.value);
  };

  React.useEffect(() => {
    const timer = setTimeout(() => _setSearchQuery(debouncedQuery), 500);
    return () => clearTimeout(timer);
  }, [debouncedQuery]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page); // Update the current page
  };

  const fetchShiftsList = React.useCallback(async () => {
    const query: any = {
      search: _searchQuery,
      page: currentPage,
      limit: itemsPerPage,
    };
    if (orderBy && order) {
      (query.sortBy = orderBy), (query.orderBy = order);
    }
    if (selectedRoleKeys) {
      query.roleId = selectedRoleKeys.join(',');
    }
    if (selectedTeamsKeys.length > 0) {
      query.teamId = selectedTeamsKeys.join(',');
    }
    try {
      setLoading(true);
      const response = await HttpService.get(
        API_ROUTES.fatchShift + `?${new URLSearchParams(query).toString()}`
      );

      setShifts(response.data.data.list || []);
      setTotalPages(response.data.data.totalPages);
    } catch (error: any) {
      console.error('Error fetching user list:', error);
    } finally {
      setLoading(false);
    }
  }, [
    selectedRoles,
    currentPage,
    itemsPerPage,
    selectedTeams,
    _searchQuery,
    order,
  ]);

  React.useEffect(() => {
    fetchShiftsList();
  }, [fetchShiftsList]);

  // Function to delete a user
  const onDeleteRow = async () => {
    if (!shiftToDelete) return;

    try {
      setLoading(true);
      await HttpService.deleteShift(
        `${API_ROUTES.deleteShift}/${shiftToDelete}`
      );

      confirm.onFalse();
      fetchShiftsList();
      toast.success('Delete Shift Successfully', {
        position: 'bottom-right',
      });
    } catch (error: any) {
      console.error('Error deleting user:', error);
      confirm.onFalse();
    } finally {
      setLoading(false);
      setShiftToDelete(null); // Reset user to delete
    }
  };

  const addShift = () => {
    router.push(PAGE_ROUTES.ADDSHIFT);
  };

  const clearFilter = () => {
    setSelectedRoleKeys([]);
    setSelectedTeamsKeys([]);
    setSelectedRoles([]);
    setSelectedTeams([]);
    setDebouncedQuery('');
    setCurrentPage(1);
  };
  const createSortHandler = (property: keyof Game) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function Row(props: {
    row: any;
    openRowId: string;
    setOpenRowId: (id: string) => void;
  }) {
    const { row } = props;

    return (
      <React.Fragment>
        <TableRow>
          <TableCell sx={{ '&:hover': { color: 'orange', cursor: 'pointer' } }}>
            {row?.shiftName}
          </TableCell>
          <TableCell sx={{ '&:hover': { color: 'orange', cursor: 'pointer' } }}>
            {dayjs(row?.startTime).format('HH:mm') || '00:00:00'}
          </TableCell>
          <TableCell sx={{ '&:hover': { color: 'orange', cursor: 'pointer' } }}>
            {dayjs(row?.endTime).format('HH:mm') || ' 00:00:00'}
          </TableCell>
          <TableCell sx={{ '&:hover': { color: 'orange', cursor: 'pointer' } }}>
            {row?.team?.userTeamName}
          </TableCell>
          <TableCell sx={{ padding: '0px', paddingLeft: '7px' }}>
            {row?.role?.roleName}
          </TableCell>

          <TableCell
            align="left"
            sx={{ margin: '0', padding: '0', whiteSpace: 'nowrap' }}
          >
            <Tooltip title="Update Shift" placement="top" arrow>
              <IconButton
                onClick={() => {
                  setCurrentShift(row); // Set the current user for quick edit
                  quickEditDetails.onTrue();
                  popover.onClose();
                }}
                sx={{ color: quickEditDetails.value ? 'inherit' : 'default' }}
              >
                <Iconify icon="tdesign:edit" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  const roleOptions = Object.entries(ROLE_MAP_filter).map(([key, value]) => ({
    key: Number(key),
    label: value,
  }));
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          padding: '20px 0',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={debouncedQuery}
            onChange={handleSearchChange}
            sx={{ width: '300px', height: '2.4375em !important' }} // Adjust the width as needed
          />
          <FormControl sx={{ minWidth: 200 }}>
            <Autocomplete
              size="small"
              multiple
              fullWidth
              id="role-selector"
              options={roleOptions}
              value={selectedRoles} // Replace with your selected roles state
              getOptionLabel={(option) => option.label}
              onChange={(event, newValue) => {
                handleChangeRole(event, newValue); // Replace with your role change handler
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Role" />
              )}
              isOptionEqualToValue={(option, value) => option.key === value.key}
            />
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <Autocomplete
              size="small"
              multiple
              fullWidth
              id="role-selector"
              options={teams}
              value={selectedTeams} // Replace with your selected roles state
              getOptionLabel={(option: any) => option.userTeamKey}
              onChange={(event, newValue) => {
                handleChangeTeams(event, newValue); // Replace with your role change handler
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Teams" />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          <Box>
            <Button
              onClick={() => clearFilter()}
              variant="contained"
              sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Clear Filter
            </Button>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={addShift}
          sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
        >
          Add Shift
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell align="left">
                <TableSortLabel
                  active={orderBy === 'shiftName'}
                  direction={orderBy === 'shiftName' ? order : 'asc'}
                  onClick={createSortHandler('shiftName')}
                >
                  Shift Name
                  {orderBy === 'shiftName' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="left">Start Time</TableCell>
              <TableCell align="left">End Time</TableCell>
              <TableCell align="left">Team</TableCell>
              <TableCell align="left">Role</TableCell>

              <TableCell align="left">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(shifts || []).map((user: any) => (
              <Row
                key={user.id}
                row={user}
                openRowId={openRowId}
                setOpenRowId={setOpenRowId}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Component */}
      <Box display="flex" justifyContent="center" my={2}>
        <Pagination
          count={totalPages} // Total number of pages
          page={currentPage} // Current page
          onChange={handlePageChange} // Handle page change
          color="primary"
        />
      </Box>

      <ShiftQuickUpdateForm
        currentShift={currentShift} // Use currentUser state here
        teams={teams}
        fetchUsersList={fetchShiftsList}
        open={quickEditDetails.value}
        onClose={quickEditDetails.onFalse}
      />
      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content="Are you sure you want to delete this user?"
        action={
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteRow}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Delete
          </Button>
        }
      />

      {/* Backdrop with CircularProgress for loader */}
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
